<template>
  <nav
    class="border-b border-border bg-bg"
    :aria-label="ariaLabel"
  >
    <div class="mx-auto flex min-h-12 w-full max-w-(--layout-max-width) items-stretch gap-1 overflow-x-auto px-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:px-6">
      <RouterLink
        v-for="link in visibleLinks"
        :key="link.routeName"
        :to="linkTo(link)"
        :ref="(element) => setLinkElement(link.routeName, element)"
        :class="navLinkClass(isActiveLink(link))"
        :aria-current="isActiveLink(link) ? 'page' : undefined"
      >
        <AppIcon v-if="link.icon" :name="link.icon" :size="14" />
        <span>{{ link.label.value }}</span>
      </RouterLink>
    </div>
  </nav>
</template>

<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import { computed, nextTick, onBeforeUpdate, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'

import { AppIcon } from '@/shared/ui'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { getPrimaryNavLinks, type NavLinkDescriptor } from '@/app/nav'
import { appShellEn } from './locales/en.ts'
import { appShellRu } from './locales/ru.ts'

const route = useRoute()
const catalog = defineLocaleCatalog({ en: appShellEn, ru: appShellRu })
const messages = useLocaleMessages(catalog)
const ariaLabel = computed<string>(() => messages.value.page.primaryNav)

const visibleLinks = computed(() => getPrimaryNavLinks().filter((link) => link.visible.value))
const linkElements = ref<ReadonlyMap<string, HTMLElement>>(new Map())
const navRenderKey = computed(() => visibleLinks.value
  .map((link) => `${link.routeName}:${link.label.value}`)
  .join('|'))

function setLinkElement(routeName: string, element: Element | ComponentPublicInstance | null): void {
  if (!(element instanceof HTMLElement)) {
    return
  }

  linkElements.value = new Map(linkElements.value).set(routeName, element)
}

function scrollActiveLinkIntoView(): void {
  const activeElement = linkElements.value.get(String(route.name))
  if (activeElement === undefined || window.matchMedia('(min-width: 640px)').matches) {
    return
  }

  activeElement.scrollIntoView({ inline: 'center', block: 'nearest' })
}

onBeforeUpdate(() => {
  linkElements.value = new Map()
})

watch(
  [() => route.name, navRenderKey, visibleLinks],
  async () => {
    await nextTick()
    scrollActiveLinkIntoView()
  },
  { immediate: true },
)

const NAV_LINK_BASE = 'inline-flex shrink-0 items-center gap-2 border-b-2 border-transparent px-3 py-3 text-sm font-semibold text-text-secondary no-underline outline-2 outline-transparent outline-offset-2 transition-[background-color,border-color,color,box-shadow] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] hover:bg-accent/6 hover:text-text-primary focus-visible:outline-accent'
const NAV_LINK_ACTIVE = 'border-accent bg-accent/10 text-accent shadow-[inset_0_-2px_0_0_color-mix(in_srgb,var(--color-accent)_45%,transparent)]'

function navLinkClass(active: boolean): string {
  return active ? `${NAV_LINK_BASE} ${NAV_LINK_ACTIVE}` : NAV_LINK_BASE
}

function isActiveLink(link: NavLinkDescriptor): boolean {
  return route.name === link.routeName
}

/**
 * Resolve a descriptor's `RouterLink :to` target. Routes with params
 * (e.g. `/:guildId/plan/:raidId`) carry a live `params` computed; routes without
 * just get `{ name }`. The `params` computed returns `undefined` while
 * the descriptor is invisible, which keeps a brief race window from
 * producing a malformed link.
 */
function linkTo(
  link: NavLinkDescriptor,
): { name: string; params?: Readonly<Record<string, string>> } {
  const params = link.params?.value
  return params !== undefined ? { name: link.routeName, params } : { name: link.routeName }
}
</script>
